import { Component, ContentChild, Input, TemplateRef, HostListener, ElementRef, OnInit, OnDestroy, AfterViewInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subscription } from 'rxjs';
import { LayoutService } from '../../../services/layout.service';

@Component({
  selector: 'app-section-card',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="section-card" #sectionCard tabindex="0">
      <div class="section-header"
           *ngIf="titleTemplate || name || actionTemplate"
           [class.sticky-header]="isSticky && sticky"
           [style.right.px]="isSticky && sticky && isSidePaneVisible ? 320 : null">
        <div class="section-title">
          <ng-container *ngIf="titleTemplate; else defaultTitle">
            <ng-container [ngTemplateOutlet]="titleTemplate"></ng-container>
          </ng-container>
          <ng-template #defaultTitle>
            {{ name }}
            <span *ngIf="description" class="block text-xs">{{description}}</span>
          </ng-template>
        </div>
        <div class="section-actions" *ngIf="actionTemplate">
          <ng-container [ngTemplateOutlet]="actionTemplate"></ng-container>
        </div>
      </div>
      <!-- Placeholder to maintain layout when header becomes fixed -->
      <div class="sticky-placeholder" *ngIf="isSticky && sticky" [style.height.px]="headerHeight"></div>
      <div class="section-content" [style]="contentPaddingStyle">
        <ng-content></ng-content>
      </div>
    </div>
  `,
  styleUrls: ['./section-card.component.scss'],
})
export class SectionCardComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() name?: string;
  @Input() description?: string;
  @Input() class?: string;
  @Input() sticky: boolean = false;
  @ContentChild('actions') actionTemplate?: TemplateRef<any>;
  @ContentChild('title') titleTemplate?: TemplateRef<any>;
  @ViewChild('sectionCard', { read: ElementRef }) sectionCardRef?: ElementRef;

  @Input() set contentPadding(value: number | number[]) {
    if (Array.isArray(value)) {
      const [vertical, horizontal] = value;
      this.contentPaddingStyle = `padding: ${vertical}px ${horizontal}px`;
    } else {
      this.contentPaddingStyle = `padding: ${value}px`;
    }
  }

  contentPaddingStyle: string = 'padding: 14px';
  isSticky = false;
  headerHeight = 55;
  isSidePaneVisible = false;
  private scrollThreshold = 100;
  private hasCalculatedThreshold = false;
  private subscription?: Subscription;

  constructor(
    private elementRef: ElementRef,
    private layoutService: LayoutService
  ) {}

  ngOnInit() {
    // Subscribe to right panel visibility changes
    this.subscription = this.layoutService.isVisible$.subscribe(
      isVisible => {
        this.isSidePaneVisible = isVisible;
      }
    );
  }

  ngAfterViewInit() {
    if (this.sticky) {
      // Calculate header height
      setTimeout(() => {
        const headerElement = this.elementRef.nativeElement.querySelector('.section-header');
        if (headerElement) {
          this.headerHeight = headerElement.offsetHeight;
        }
      }, 0);
    }
  }

  ngOnDestroy() {
    // Unsubscribe to prevent memory leaks
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  @HostListener('window:scroll')
  onWindowScroll() {
    if (!this.sticky) return;

    // Calculate or recalculate the threshold if not yet calculated or if element was previously hidden
    if (!this.hasCalculatedThreshold && this.sectionCardRef) {
      const cardElement = this.sectionCardRef.nativeElement;
      const rect = cardElement.getBoundingClientRect();

      // Only calculate if element is visible and has dimensions
      if (rect.height > 0 && rect.width > 0) {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        this.scrollThreshold = rect.top + scrollTop;
        this.hasCalculatedThreshold = true;
      }
    }

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;

    // Only make sticky if scrolled past the threshold
    this.isSticky = scrollTop > this.scrollThreshold;
  }

  // Public method to recalculate threshold when tab becomes visible
  public recalculatePosition(): void {
    this.hasCalculatedThreshold = false;
    if (this.sectionCardRef) {
      const cardElement = this.sectionCardRef.nativeElement;
      const rect = cardElement.getBoundingClientRect();

      if (rect.height > 0 && rect.width > 0) {
        const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        this.scrollThreshold = rect.top + scrollTop;
        this.hasCalculatedThreshold = true;
      }
    }
  }
}
